import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { QuestionDifficulty } from '../../api/@types';
import { auth } from '../../client/firebase';

export class MedqContentsClient {
  private readonly axiosClient: AxiosInstance;

  constructor(config: {
    endpoint?: string;
  }) {
    this.axiosClient = axios.create({
      baseURL: config.endpoint,
    });
    this.createRefreshTokenInterceptor();
  }

  async institutionByRegion(): Promise<InstitutionByRegionResponse> {
    const result = await this.axiosClient.get<InstitutionByRegionResponse>('/info/institution/by-region');
    return result.data ?? [];
  }

  async questionById(id: string): Promise<QuestionResult | undefined> {
    const result = await this.axiosClient.get<QuestionInfoResponse>(`/info/question/${id}`);
    return this.parseQuestion(result.data);
  }

  async questionsByIds(ids: string[]): Promise<QuestionResult[]> {
    const result = await this.axiosClient.get<QuestionInfoResponse[]>('/info/question', {
      params: {
        ids,
      },
    });
    return result.data.map((e) => this.parseQuestion(e));
  }

  async examsByInstitution(institutionId: string): Promise<ExamResult[]> {
    const result = await this.axiosClient.get<ExamResponse[]>('/manager/exam/', {
      params: { institutionId },
    });
    return result.data;
  }

  async getExamTypesAndComplements(): Promise<ExamTypesAndComplementsResult> {
    const result = await this.axiosClient.get<ExamTypesAndComplementsResult>('/manager/exam/types-and-specialties');
    return result.data;
  }

  async listAllSubjects(): Promise<SubjectInfo[]> {
    const result = await this.axiosClient.get<SubjectInfo[]>('/info/subject/');
    return result.data;
  }

  private parseQuestion(e: QuestionInfoResponse): QuestionResult {
    return {
      id: e.id,
      subject: e.subject,
      body: e.body,
      answers: e.answers.map((answer) => answer.body),
      year: e.examYear,
      answersProportion: e.answers.map((answer, index) => ({
        answerIndex: index,
        count: answer.answeredCount,
      })),
      correctAnswer: e.answers.findIndex((answer) => answer.isCorrect),
      difficulty: e.difficulty.toUpperCase() as QuestionDifficulty,
      institutionName: e.institutionName,
    };
  }

  private async getToken(): Promise<string> {
    const { currentUser } = auth;
    const res = await currentUser?.getIdToken();
    return res || '';
  }

  private createRefreshTokenInterceptor(): void {
    this.axiosClient.interceptors.request.use(async (request: AxiosRequestConfig) => {
      const token = await this.getToken();
      request.headers.Authorization = `Bearer ${token}`;
      return request;
    });
  }
}

export type SubjectInfo = {
  subjectId: string;
  subjectName: string;
  subjectAccumulatedNames: string[];
  ancestorsIds: string[];
  childrenIds: string[];
  difficulty: number;
}

export type CountGroupByQuestionStyle = {
  style: string;
  count: number;
};

export type InstitutionByRegionResponse = {
  region: string;
  institutions: {
    id: string;
    name: string;
  }[];
}[];

export type QuestionResult = Readonly<{
  id: string;
  subject: string;
  body: string;
  answers: string[];
  year: number;
  answersProportion: {
    answerIndex: number;
    count: number;
  }[];
  correctAnswer: number;
  institutionName: string;
  difficulty: QuestionDifficulty;
}>;

export type ExamResult = ExamResponse;

type ExamResponse = {
  id: string;
  type: string;
  year: number;
  preRequisite?: string;
  institutionId: string;
};

type QuestionInfoResponse = {
  id: string;
  subject: string;
  body: string;
  answers: AnswerInfo[];
  examYear: number;
  institutionName: string;
  difficulty: string;
};

type AnswerInfo = {
  body: string;
  isCorrect: boolean;
  answeredCount: number;
};

type ExamTypesAndComplementsResult = {
  types: string[];
  specialties: string[];
}

export type SpecialtyInfo = {
  id: string;
  name: string;
  type: string;
}
